import { Box, TextField, Button, InputAdornment, IconButton } from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useState, useEffect, useRef, useCallback } from "react";
import { addDaysToDate, resetToFriday } from "../../utils/dateUtils";

interface WeekDatePickerProps {
  startDate: Date;
  setStartDate: (date: Date) => void;
  onApply?: (date: Date) => void;
  label?: string;
}

// Format: JJ-MM-AAAA
const formatInputDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Parse: JJ-MM-AAAA ou AAAA-MM-JJ
const parseInputDate = (val: string): Date => {
  // Si format JJ-MM-AAAA
  if (val.match(/^\d{2}-\d{2}-\d{4}$/)) {
    const [day, month, year] = val.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0);
  }
  // Si format AAAA-MM-JJ (fallback)
  if (val.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return new Date(val + "T00:00:00");
  }
  const parsed = new Date(val);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
};

const WeekDatePicker = ({ startDate, setStartDate, onApply, label = "Début de la période" }: WeekDatePickerProps) => {
  const [inputValue, setInputValue] = useState(formatInputDate(startDate));
  const [dateError, setDateError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const autoFormatDate = (value: string, previousValue: string): string => {
    if (value.length < previousValue.length) {
      return value;
    }

    const cleaned = value.replace(/[^\d-]/g, '');
    
    if (cleaned.includes('-')) {
      return cleaned;
    }

    const numbers = cleaned.replace(/-/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 4)}-${numbers.slice(4, 8)}`;
    }
  };

  useEffect(() => {
    setInputValue(formatInputDate(startDate));
  }, [startDate]);

  useEffect(() => {
    if (inputValue && !inputValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
      setDateError("Format attendu: JJ-MM-AAAA");
    } else {
      setDateError(null);
    }
  }, [inputValue]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = autoFormatDate(value, inputValue);
    setInputValue(formatted);
    
    if (formatted.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const selectedDate = parseInputDate(formatted);
      if (!isNaN(selectedDate.getTime())) {
        const alignedDate = resetToFriday(selectedDate);
        
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          setStartDate(alignedDate);
        }, 3000);
      }
    }
  };

  const handleApply = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const selectedDate = parseInputDate(inputValue);
    if (!isNaN(selectedDate.getTime())) {
      const alignedDate = resetToFriday(selectedDate);
      if (onApply) {
        onApply(alignedDate);
      } else {
        setStartDate(alignedDate);
      }
    }
  }, [inputValue, setStartDate, onApply]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleApply();
    }
  };

  const handleWeekShift = (days: number) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const newDate = addDaysToDate(startDate, days);
    const alignedDate = resetToFriday(newDate);
    
    if (onApply) {
      onApply(alignedDate);
    } else {
      setStartDate(alignedDate);
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <Box position="relative">
        <TextField
          type="text"
          label={label}
          placeholder="JJ-MM-AAAA"
          value={inputValue}
          onChange={handleDateChange}
          onBlur={handleApply}
          onKeyDown={handleKeyDown}
          InputLabelProps={{ shrink: true }}
          error={!!dateError}
          helperText={dateError || "Format: JJ-MM-AAAA"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  size="small"
                  onClick={() => {
                    const input = document.getElementById('hidden-date-week') as HTMLInputElement;
                    input?.showPicker();
                  }}
                  sx={{ padding: 0 }}
                >
                  <CalendarTodayIcon fontSize="small" color="action" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <input
          id="hidden-date-week"
          type="date"
          style={{
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            width: 0,
            height: 0,
          }}
          value={inputValue.match(/^\d{2}-\d{2}-\d{4}$/) ? inputValue.split('-').reverse().join('-') : ''}
          onChange={(e) => {
            if (e.target.value) {
              const [year, month, day] = e.target.value.split('-');
              const formatted = `${day}-${month}-${year}`;
              setInputValue(formatted);
              const selectedDate = parseInputDate(formatted);
              if (!isNaN(selectedDate.getTime())) {
                const alignedDate = resetToFriday(selectedDate);
                if (onApply) {
                  onApply(alignedDate);
                } else {
                  setStartDate(alignedDate);
                }
              }
            }
          }}
        />
      </Box>

      <Box display="flex" gap={2}>
        <Button variant="outlined" onClick={() => handleWeekShift(-7)}>
          Semaine précédente
        </Button>
        <Button variant="outlined" onClick={() => handleWeekShift(7)}>
          Semaine suivante
        </Button>
      </Box>
    </Box>
  );
};

export default WeekDatePicker;
